import React, { useCallback, useEffect, useState } from 'react';

import styles from './styles.module.css';
import { Alert, Checkbox } from '@clearcover/atlas';
import { usePurchaseContext } from 'QuoteApp/services/providers';

import {
  Disclosures,
  GetDisclosuresResponseType,
} from 'QuoteApp/services/endpoints/GetDisclosures';
import { LoadingPage } from 'Shared/components';

type DisclosureType = GetDisclosuresResponseType & {
  indentifier:
    | Disclosures.AgentSignatureAffirmation
    | Disclosures.AgentBindAuthorization
    | Disclosures.AgentTOS
    | Disclosures.VehicleSetup;
};

export const AgentConfirmation = () => {
  const {
    setAgentConfirmation,
    eSignatureErrors,
    setESignatureErrors,
    agentSignatureAffirmation,
    agentSignatureAffirmationDataLoading,
    agentBindAuthorization,
    agentBindAuthorizationDataLoading,
    agentTOS,
    agentTOSDataLoading,
    vehiclePhotos,
    vehiclePhotosDataLoading,
  } = usePurchaseContext();
  const [disclosures, setDisclosures] = useState<DisclosureType[]>([]);

  // Vehicle photos feature is only available for non-standard auto product segment
  const isNonStandardAutoProduct = !!vehiclePhotos?.dueDate;

  const [disclosureState, setDisclosureState] = useState<{
    [key: string]: boolean;
  } | null>(null);

  const getVehicleSetupDisclosure = useCallback(
    (): DisclosureType => ({
      // Persistence is not required for this disclosure; it exists solely for the frontend.
      completed: false,
      indentifier: Disclosures.VehicleSetup,
      bodyTextSanitized: {
        __html:
          'I understand that this customer must complete vehicle setup in the Clearcover mobile app.',
      },
    }),
    []
  );

  useEffect(() => {
    if (
      disclosures.length === 0 &&
      agentSignatureAffirmation &&
      agentBindAuthorization &&
      agentTOS
    ) {
      setDisclosures([
        {
          ...agentBindAuthorization,
          indentifier: Disclosures.AgentBindAuthorization,
        },
        {
          ...agentSignatureAffirmation,
          indentifier: Disclosures.AgentSignatureAffirmation,
        },
        { ...agentTOS, indentifier: Disclosures.AgentTOS },
      ]);
    }
  }, [
    agentSignatureAffirmation,
    agentBindAuthorization,
    agentTOS,
    disclosures,
  ]);

  useEffect(() => {
    if (disclosures.length > 0 && vehiclePhotos) {
      const vehicleSetupDisclosure = getVehicleSetupDisclosure();
      const hasVehicleSetupDisclosure = disclosures.some(
        d => d.indentifier === Disclosures.VehicleSetup
      );

      if (isNonStandardAutoProduct && !hasVehicleSetupDisclosure) {
        setDisclosures(prevState => [
          prevState[0],
          vehicleSetupDisclosure,
          ...prevState.slice(1),
        ]);
      }
    }
  }, [
    disclosures,
    vehiclePhotos,
    disclosures.length,
    isNonStandardAutoProduct,
    getVehicleSetupDisclosure,
  ]);

  useEffect(() => {
    if (
      agentSignatureAffirmation &&
      agentBindAuthorization &&
      agentTOS &&
      disclosures.length > 0 &&
      !disclosureState
    ) {
      setDisclosureState(
        Object.fromEntries(disclosures.map(e => [e.indentifier, e.completed]))
      );
    }
  }, [
    agentSignatureAffirmation,
    agentBindAuthorization,
    agentTOS,
    disclosureState,
    disclosures,
  ]);

  useEffect(() => {
    if (disclosureState) {
      const allCompleted =
        Object.values(disclosureState).filter(completed => !!completed)
          .length === Object.values(disclosures).length;

      if (allCompleted && eSignatureErrors?.agentConfirmation) {
        const updatedErrors = { ...eSignatureErrors };
        delete updatedErrors.agentConfirmation;
        setESignatureErrors(updatedErrors);
      }
      setAgentConfirmation(allCompleted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disclosureState, disclosures]);

  return (
    <div className={styles.agentConfirmationContainer}>
      <h4>Complete purchase</h4>
      {agentSignatureAffirmationDataLoading ||
      agentBindAuthorizationDataLoading ||
      agentTOSDataLoading ||
      vehiclePhotosDataLoading ? (
        <LoadingPage />
      ) : (
        <>
          {disclosures.map(item => (
            <div
              className={styles.agentConfirmationDisclosureContainer}
              key={`e-signature-disclosure-${item.indentifier}`}
            >
              <Checkbox
                label={item.bodyTextSanitized.__html}
                name={item.indentifier}
                value={disclosureState?.[item.indentifier] || false}
                onChange={value =>
                  setDisclosureState(prevState => ({
                    ...prevState,
                    [item.indentifier]: value,
                  }))
                }
                hideLabel
                disabled={item.completed && disclosureState?.[item.indentifier]}
              />
              <label
                className={styles.agentConfirmationDisclosureCheckboxLabel}
                htmlFor={`${item.indentifier}_${disclosureState?.[
                  item.indentifier
                ] || false}`}
                dangerouslySetInnerHTML={{
                  __html: item.bodyTextSanitized.__html,
                }}
              />
            </div>
          ))}
          {eSignatureErrors?.agentConfirmation && (
            <div data-testid="esignature-confirmation-error-notification">
              <Alert
                message={eSignatureErrors.agentConfirmation}
                className={styles.eSignatureError}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
